/**
 * Page /petite-enfance — Module Petite Enfance.
 *
 * Lot 1 (socle) : Vue d'ensemble · Structures d'accueil · Dossiers.
 * Les onglets Présences/Encadrement, Facturation, RAM et Pré-inscriptions
 * arriveront aux lots suivants (placeholders « à venir »).
 *
 * Accent rose tendre (hue ~350) propre au secteur petite enfance.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PetiteEnfanceLayout } from '@/components/petite-enfance/PetiteEnfanceLayout';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import type { PeStructure, PeDossier, PeDashboardData, PeStructureType, PeContrat, PeContratType, PePresence, PeEncadrementJour, PePlanningCreneau, PeParametres, PeBaremePsuLigne, PeFactureApercu, PeFacture, PeAssmat, PeAtelier, PeAtelierInscription, PePreinscription, PePreinscStatut, PeEnfantDisponible, PeJourOuverture } from '@/lib/api';
import {
  Baby, Plus, AlertCircle, Building2, Users, HeartPulse, CalendarClock, Wallet, HandHeart, ClipboardList,
  FileText, Mail, Download, Upload, Search, UserPlus, Settings, ChevronLeft, ChevronRight, Check, X, MapPin,
} from 'lucide-react';
import { LieuSelect } from '@/components/LieuSelect';

const ACCENT = 'oklch(0.66 0.16 350)'; // rose tendre

async function downloadBlob(getBlob: () => Promise<Blob>, filename: string) {
  const blob = await getBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

const STRUCTURE_TYPES: { value: PeStructureType; label: string }[] = [
  { value: 'multi_accueil', label: 'Multi-accueil' },
  { value: 'micro_creche', label: 'Micro-crèche' },
  { value: 'halte_garderie', label: 'Halte-garderie' },
  { value: 'creche_collective', label: 'Crèche collective' },
  { value: 'creche_familiale', label: 'Crèche familiale' },
];
function typeLabel(t: string): string {
  return STRUCTURE_TYPES.find((x) => x.value === t)?.label ?? t;
}

type Tab = 'ensemble' | 'structures' | 'dossiers' | 'contrats' | 'presences' | 'facturation' | 'ram' | 'preinscriptions' | 'tarifs' | 'calendrier' | 'parametres';

const VALID_TABS: Tab[] = ['ensemble', 'structures', 'dossiers', 'contrats', 'presences', 'facturation', 'ram', 'preinscriptions', 'tarifs', 'calendrier', 'parametres'];

export default function PetiteEnfancePage() {
  const params = useParams<{ tab?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const rawTab = (params.tab ?? 'ensemble') as Tab;
  const tab: Tab = VALID_TABS.includes(rawTab) ? rawTab : 'ensemble';
  const isAdmin = !!user && ['super_admin', 'admin'].includes(user.role);

  const [error, setError] = useState<string | null>(null);
  const [dash, setDash] = useState<PeDashboardData | null>(null);
  const [structures, setStructures] = useState<PeStructure[]>([]);
  const [dossiers, setDossiers] = useState<PeDossier[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, s, dos] = await Promise.all([
        api.petiteEnfance.dashboard(),
        api.petiteEnfance.structures.list(),
        api.petiteEnfance.dossiers.list(),
      ]);
      setDash(d);
      setStructures(s.structures);
      setDossiers(dos.dossiers);
    } catch {
      setError('Impossible de charger le module petite enfance.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Garde-fou : les sections de config (tarifs/calendrier/paramètres) sont
  // réservées admin. Un non-admin qui force l'URL est renvoyé au tableau de bord.
  useEffect(() => {
    if (!isAdmin && (tab === 'tarifs' || tab === 'calendrier' || tab === 'parametres')) {
      navigate('/petite-enfance', { replace: true });
    }
  }, [isAdmin, tab, navigate]);

  return (
    <PetiteEnfanceLayout activeTab={tab}>
      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5">
        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

        {loading ? (
          <Card variant="glass" padded>
            <div className="py-10 text-center">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: ACCENT, borderTopColor: 'transparent' }} role="status" aria-label="Chargement" />
            </div>
          </Card>
        ) : (
          <>
            {tab === 'ensemble' && dash && <VueEnsemble dash={dash} />}
            {tab === 'structures' && <Structures structures={structures} onChange={load} setError={setError} />}
            {tab === 'dossiers' && <Dossiers dossiers={dossiers} onChange={load} setError={setError} />}
            {tab === 'contrats' && <Contrats structures={structures} dossiers={dossiers} setError={setError} />}
            {tab === 'presences' && <PresencesEncadrement structures={structures} dossiers={dossiers} setError={setError} />}
            {tab === 'facturation' && <Facturation structures={structures} setError={setError} />}
            {tab === 'ram' && <Ram setError={setError} />}
            {tab === 'preinscriptions' && <Preinscriptions structures={structures} setError={setError} />}
            {tab === 'tarifs' && <TarifsPe setError={setError} />}
            {tab === 'calendrier' && <CalendrierPe structures={structures} setError={setError} />}
            {tab === 'parametres' && <ParametresPe setError={setError} />}
          </>
        )}
      </main>
    </PetiteEnfanceLayout>
  );
}

// ── Vue d'ensemble ───────────────────────────────────────────────────────────

function VueEnsemble({ dash }: { dash: PeDashboardData }) {
  const kpis = [
    { label: 'Structures actives', value: dash.nbStructures, icon: Building2 },
    { label: 'Places agréées', value: dash.capaciteTotale, icon: Users },
    { label: 'Enfants inscrits', value: dash.nbEnfantsInscrits, icon: Baby },
    { label: 'Dossiers complétés', value: dash.nbDossiers, icon: HeartPulse },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {kpis.map((k, i) => (
        <div key={i} className="rounded-r-md border border-border bg-surface/40 p-4 backdrop-blur-glass">
          <k.icon className="h-5 w-5" style={{ color: ACCENT }} aria-hidden="true" />
          <div className="mt-2 text-2xl font-semibold tabular-nums text-ink">{k.value}</div>
          <div className="text-xs text-ink-subtle">{k.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Structures ───────────────────────────────────────────────────────────────

function Structures({
  structures, onChange, setError,
}: {
  structures: PeStructure[];
  onChange: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const [show, setShow] = useState(false);
  const { user } = useAuth();
  // Création/désactivation de structure = config réservée admin/maire
  // (aligné sur Scolaire écoles/classes et le garde-fou backend requireRole).
  const isAdmin = user ? ['super_admin', 'admin'].includes(user.role) : false;
  const [nom, setNom] = useState('');
  const [type, setType] = useState<PeStructureType>('multi_accueil');
  const [capacite, setCapacite] = useState('');
  const [debut, setDebut] = useState('07:30');
  const [fin, setFin] = useState('18:30');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!nom.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.petiteEnfance.structures.create({
        nom: nom.trim(), type, capacite: parseInt(capacite, 10) || 0,
        amplitudeDebut: debut || null, amplitudeFin: fin || null,
      });
      setNom(''); setCapacite(''); setShow(false);
      await onChange();
    } catch {
      setError('La structure n\'a pas pu être créée.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActif = async (s: PeStructure) => {
    try {
      await api.petiteEnfance.structures.update(s.id, { actif: !s.actif });
      await onChange();
    } catch {
      setError('Modification impossible.');
    }
  };

  const rattacherLieu = async (structureId: string, lieuId: string | null) => {
    try {
      await api.petiteEnfance.structures.update(structureId, { lieuId });
      await onChange();
    } catch {
      setError('Rattachement au lieu impossible.');
    }
  };

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-ink-muted">{structures.length} structure(s)</p>
        {isAdmin && (
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => setShow(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle structure
          </Button>
        )}
      </div>

      {structures.length === 0 ? (
        <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Aucune structure d'accueil.</p></Card>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {structures.map((s) => (
            <Card key={s.id} variant="glass" className={`p-4 ${s.actif ? '' : 'opacity-60'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-ink">{s.nom}</div>
                  <div className="text-xs text-ink-subtle">{typeLabel(s.type)}</div>
                </div>
                {!s.actif && <Badge variant="neutral" size="sm">Inactive</Badge>}
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm">
                <span className="text-ink-muted"><span className="font-semibold text-ink">{s.capacite}</span> places</span>
                {s.amplitudeDebut && s.amplitudeFin && (
                  <span className="text-ink-muted">{s.amplitudeDebut} – {s.amplitudeFin}</span>
                )}
              </div>
              {isAdmin && (
                <button type="button" onClick={() => void toggleActif(s)} className="mt-2 text-xs text-ink-muted underline hover:text-ink">
                  {s.actif ? 'Désactiver' : 'Réactiver'}
                </button>
              )}
              {isAdmin && (
                <div className="mt-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
                  <span className="text-xs text-ink-muted">Bâtiment :</span>
                  <LieuSelect
                    value={s.lieuId}
                    onChange={(lieuId) => void rattacherLieu(s.id, lieuId)}
                    className="h-8 max-w-[16rem] rounded-lg border border-border bg-surface/60 px-2 text-xs text-ink"
                  />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={show} onClose={() => setShow(false)} title="Nouvelle structure d'accueil" size="md">
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="st-nom">Nom</label>
            <input id="st-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Multi-accueil Les Petits Pas"
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="st-type">Type</label>
              <select id="st-type" value={type} onChange={(e) => setType(e.target.value as PeStructureType)}
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
                {STRUCTURE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="st-cap">Places agréées</label>
              <input id="st-cap" value={capacite} onChange={(e) => setCapacite(e.target.value)} inputMode="numeric" placeholder="20"
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="st-deb">Ouverture</label>
              <input id="st-deb" type="time" value={debut} onChange={(e) => setDebut(e.target.value)}
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="st-fin">Fermeture</label>
              <input id="st-fin" type="time" value={fin} onChange={(e) => setFin(e.target.value)}
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => setShow(false)}>Annuler</Button>
            <Button type="button" variant="petite_enfance" size="sm" onClick={() => void submit()} disabled={saving || !nom.trim()}>
              {saving ? 'Enregistrement…' : 'Créer'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

// ── Dossiers ─────────────────────────────────────────────────────────────────

function Dossiers({
  dossiers, onChange, setError,
}: {
  dossiers: PeDossier[];
  onChange: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const { user } = useAuth();
  const peutGerer = user ? ['super_admin', 'admin', 'manager', 'agent'].includes(user.role) : false;
  const [repriseOpen, setRepriseOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editDossier, setEditDossier] = useState<PeDossier | null>(null);

  const exporter = async (format: 'csv' | 'pdf') => {
    setExporting(true); setError(null);
    try {
      const today = new Date();
      const d = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      await downloadBlob(() => api.petiteEnfance.dossiers.exportBlob(format), `enfants-petite-enfance-${d}.${format}`);
    } catch {
      setError("L'export a échoué.");
    } finally { setExporting(false); }
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink-muted">{dossiers.length} dossier(s)</p>
        {peutGerer && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="petite_enfance" size="sm" onClick={() => setRepriseOpen(true)}>
              <UserPlus className="h-4 w-4" aria-hidden="true" /> Reprendre un enfant connu
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" aria-hidden="true" /> Import CSV
            </Button>
            <Button type="button" variant="secondary" size="sm" disabled={exporting || dossiers.length === 0} onClick={() => void exporter('csv')}>
              <Download className="h-4 w-4" aria-hidden="true" /> CSV
            </Button>
            <Button type="button" variant="secondary" size="sm" disabled={exporting || dossiers.length === 0} onClick={() => void exporter('pdf')}>
              <Download className="h-4 w-4" aria-hidden="true" /> PDF
            </Button>
          </div>
        )}
      </div>

      <p className="mb-3 text-xs text-ink-subtle">
        Les dossiers reprennent les enfants et familles déjà connus de la mairie (cantine, école…) sans double saisie,
        et y ajoutent les informations médicales et autorisations propres à l'accueil du jeune enfant.
      </p>

      {dossiers.length === 0 ? (
        <Card variant="glass" padded>
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <HeartPulse className="h-8 w-8 text-ink-subtle" aria-hidden="true" />
            <p className="text-sm text-ink-muted">Aucun dossier petite enfance pour l'instant.</p>
            <p className="text-xs text-ink-subtle">Utilisez « Reprendre un enfant connu » pour rattacher un enfant déjà en base, ou « Import CSV » pour une reprise en masse.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {dossiers.map((d) => (
            <Card key={d.id} variant="glass" className={`p-3.5${peutGerer ? ' cursor-pointer transition hover:border-border-hi' : ''}`}
              onClick={peutGerer ? () => setEditDossier(d) : undefined}
              role={peutGerer ? 'button' : undefined} tabIndex={peutGerer ? 0 : undefined}
              onKeyDown={peutGerer ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setEditDossier(d); } } : undefined}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full" style={{ background: `color-mix(in oklab, ${ACCENT} 18%, transparent)` }}>
                  <Baby className="h-5 w-5" style={{ color: ACCENT }} aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink">{d.enfantPrenom} {d.enfantNom}</div>
                  <div className="text-[11px] text-ink-subtle">
                    {d.enfantDateNaissance ? `Né(e) le ${new Date(d.enfantDateNaissance + 'T00:00:00').toLocaleDateString('fr-FR')}` : 'Date de naissance non renseignée'}
                    {d.medecinNom ? ` · Dr ${d.medecinNom}` : ''}
                    {peutGerer ? ' · cliquer pour modifier' : ''}
                  </div>
                </div>
                <div className="flex flex-shrink-0 flex-wrap gap-1.5">
                  {d.vaccinationsAJour
                    ? <Badge variant="success" size="sm">Vaccins à jour</Badge>
                    : <Badge variant="warning" size="sm">Vaccins à vérifier</Badge>}
                  <Badge variant="neutral" size="sm">{d.marche ? 'Marche' : 'Ne marche pas'}</Badge>
                  {d.paiDetail && <Badge variant="warning" size="sm">PAI</Badge>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {repriseOpen && (
        <RepriseEnfantDialog
          onClose={() => setRepriseOpen(false)}
          onDone={async () => { setRepriseOpen(false); await onChange(); }}
          setError={setError}
        />
      )}
      {editDossier && (
        <DossierEditForm
          dossier={editDossier}
          onClose={() => setEditDossier(null)}
          onSaved={async () => { setEditDossier(null); await onChange(); }}
          setError={setError}
        />
      )}
      {importOpen && (
        <ImportEnfantsPeDialog
          onClose={() => setImportOpen(false)}
          onDone={async () => { setImportOpen(false); await onChange(); }}
          setError={setError}
        />
      )}
    </>
  );
}

/** Édition du dossier PMI (médecin, vaccins, marche, PAI, autorisations). */
function DossierEditForm({ dossier, onClose, onSaved, setError }: {
  dossier: PeDossier; onClose: () => void; onSaved: () => Promise<void>; setError: (s: string | null) => void;
}) {
  const [f, setF] = useState({
    medecinNom: dossier.medecinNom ?? '', medecinTel: dossier.medecinTel ?? '',
    vaccinationsAJour: dossier.vaccinationsAJour, marche: dossier.marche,
    paiDetail: dossier.paiDetail ?? '', autorisePhoto: dossier.autorisePhoto,
    autoriseSortie: dossier.autoriseSortie, personnesAutorisees: dossier.personnesAutorisees ?? '',
    notes: dossier.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.dossiers.upsert({
        enfantId: dossier.enfantId,
        medecinNom: f.medecinNom.trim() || null, medecinTel: f.medecinTel.trim() || null,
        vaccinationsAJour: f.vaccinationsAJour, marche: f.marche,
        paiDetail: f.paiDetail.trim() || null, autorisePhoto: f.autorisePhoto,
        autoriseSortie: f.autoriseSortie, personnesAutorisees: f.personnesAutorisees.trim() || null,
        notes: f.notes.trim() || null,
      });
      await onSaved();
    } catch { setError("Enregistrement du dossier impossible."); }
    finally { setSaving(false); }
  };
  const inputCls = 'w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none';
  return (
    <Dialog open onClose={onClose} title={`Dossier de ${dossier.enfantPrenom} ${dossier.enfantNom}`} size="md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="do-med">Médecin</label>
            <input id="do-med" value={f.medecinNom} onChange={(e) => setF({ ...f, medecinNom: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="do-tel">Téléphone médecin</label>
            <input id="do-tel" value={f.medecinTel} onChange={(e) => setF({ ...f, medecinTel: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={f.vaccinationsAJour} onChange={(e) => setF({ ...f, vaccinationsAJour: e.target.checked })} className="h-4 w-4" />
            Vaccinations à jour
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={f.marche} onChange={(e) => setF({ ...f, marche: e.target.checked })} className="h-4 w-4" />
            L'enfant marche
          </label>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="do-pai">PAI (projet d'accueil individualisé)</label>
          <textarea id="do-pai" value={f.paiDetail} onChange={(e) => setF({ ...f, paiDetail: e.target.value })} rows={2} placeholder="Allergies, traitements, protocole… (laisser vide si non concerné)" className={inputCls} />
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={f.autorisePhoto} onChange={(e) => setF({ ...f, autorisePhoto: e.target.checked })} className="h-4 w-4" />
            Autorise les photos
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={f.autoriseSortie} onChange={(e) => setF({ ...f, autoriseSortie: e.target.checked })} className="h-4 w-4" />
            Autorise les sorties
          </label>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="do-pers">Personnes autorisées à récupérer l'enfant</label>
          <input id="do-pers" value={f.personnesAutorisees} onChange={(e) => setF({ ...f, personnesAutorisees: e.target.value })} placeholder="Noms, lien de parenté…" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="do-notes">Notes</label>
          <textarea id="do-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className={inputCls} />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void submit()} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer le dossier'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Libellé + couleur de tranche d'âge. */
const TRANCHE_META: Record<PeEnfantDisponible['tranche'], { label: string; variant: 'success' | 'warning' | 'neutral' }> = {
  moins_4: { label: '< 4 ans', variant: 'success' },
  '4_6':   { label: '4–6 ans', variant: 'warning' },
  inconnu: { label: 'Âge inconnu', variant: 'neutral' },
};
const SERVICE_LABEL_COURT: Record<string, string> = {
  cantine: 'cantine', periscolaire: 'périscolaire', alsh: 'ALSH',
};

function RepriseEnfantDialog({
  onClose, onDone, setError,
}: {
  onClose: () => void;
  onDone: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const [enfants, setEnfants] = useState<PeEnfantDisponible[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const r = await api.petiteEnfance.dossiers.enfantsDisponibles();
        if (on) setEnfants(r.enfants);
      } catch {
        if (on) setError("Impossible de charger les enfants disponibles.");
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [setError]);

  const filtres = q.trim().toLowerCase();
  const liste = filtres
    ? enfants.filter((e) => `${e.prenom} ${e.nom} ${e.familleNom}`.toLowerCase().includes(filtres))
    : enfants;

  const reprendre = async (e: PeEnfantDisponible) => {
    setSaving(e.enfantId); setError(null);
    try {
      await api.petiteEnfance.dossiers.upsert({ enfantId: e.enfantId });
      await onDone();
    } catch {
      setError("La reprise de l'enfant a échoué.");
      setSaving(null);
    }
  };

  return (
    <Dialog open onClose={onClose} title="Reprendre un enfant connu" size="lg">
      <p className="mb-3 text-xs text-ink-subtle">
        Enfants déjà connus de la mairie, de moins de 6 ans, sans dossier petite enfance.
        Cliquez pour créer leur dossier — les autres services (cantine, école) ne sont pas modifiés.
      </p>
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" aria-hidden="true" />
        <input
          type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un enfant ou une famille…"
          className="h-10 w-full rounded-r-lg border border-border bg-surface/60 pl-10 pr-3 text-sm text-ink focus:border-petite_enfance focus:outline-none focus:ring-2 focus:ring-petite_enfance focus:ring-offset-2 focus:ring-offset-bg"
        />
      </div>
      {loading ? (
        <div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div>
      ) : liste.length === 0 ? (
        <div className="py-8 text-center text-sm text-ink-subtle">
          {enfants.length === 0 ? 'Aucun enfant éligible (tous ont déjà un dossier, ou ont 6 ans et plus).' : 'Aucun résultat.'}
        </div>
      ) : (
        <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
          {liste.map((e) => {
            const tr = TRANCHE_META[e.tranche];
            return (
              <div key={e.enfantId} className="flex items-center gap-3 rounded-r-lg border border-border bg-surface/40 p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink">{e.prenom} {e.nom}</div>
                  <div className="text-[11px] text-ink-subtle">
                    Famille {e.familleNom}
                    {e.dateNaissance ? ` · né(e) le ${new Date(e.dateNaissance + 'T00:00:00').toLocaleDateString('fr-FR')}` : ' · date de naissance non renseignée'}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge variant={tr.variant} size="sm">{tr.label}</Badge>
                    {e.servicesActuels.map((s) => (
                      <Badge key={s} variant="neutral" size="sm">déjà en {SERVICE_LABEL_COURT[s] ?? s}</Badge>
                    ))}
                  </div>
                </div>
                <Button type="button" variant="petite_enfance" size="sm" disabled={saving === e.enfantId} onClick={() => void reprendre(e)}>
                  {saving === e.enfantId ? 'Reprise…' : 'Reprendre'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Fermer</Button>
      </div>
    </Dialog>
  );
}

function ImportEnfantsPeDialog({
  onClose, onDone, setError,
}: {
  onClose: () => void;
  onDone: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const [contenu, setContenu] = useState('');
  const [saving, setSaving] = useState(false);
  const [resultat, setResultat] = useState<{ enfantsCrees: number; famillesCreees: number; ignores: number; erreurs: number } | null>(null);

  const onFile = (f: File | null) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setContenu(String(reader.result ?? ''));
    reader.readAsText(f);
  };

  const lancer = async () => {
    if (!contenu.trim()) return;
    setSaving(true); setError(null);
    try {
      const r = await api.petiteEnfance.dossiers.importCsv(contenu);
      setResultat({ enfantsCrees: r.enfantsCrees, famillesCreees: r.famillesCreees, ignores: r.ignores, erreurs: r.erreurs });
    } catch {
      setError("L'import a échoué. Vérifiez l'en-tête : nom_famille, prenom_enfant, nom_enfant.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} title="Import CSV — enfants petite enfance" size="md">
      <p className="mb-2 text-xs text-ink-subtle">
        En-tête attendu (séparateur ; ou ,) :
        <code className="ml-1 rounded bg-surface/60 px-1">nom_famille;prenom_enfant;nom_enfant;date_naissance</code>.
        La famille est créée si elle n'existe pas. Les enfants importés sont inscrits en petite enfance.
      </p>
      <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0] ?? null)} className="mb-2 block w-full text-sm text-ink file:mr-3 file:rounded-r-md file:border-0 file:bg-petite_enfance/15 file:px-3 file:py-1.5 file:text-sm file:text-petite_enfance-fg" />
      <textarea
        value={contenu} onChange={(e) => setContenu(e.target.value)} rows={6}
        placeholder="…ou collez le contenu CSV ici"
        className="mb-3 w-full rounded-r-lg border border-border bg-surface/60 p-2.5 text-xs text-ink focus:border-petite_enfance focus:outline-none focus:ring-2 focus:ring-petite_enfance"
      />
      {resultat && (
        <Alert variant={resultat.erreurs > 0 ? 'warning' : 'success'} icon={AlertCircle} className="mb-3">
          {resultat.enfantsCrees} créé(s), {resultat.famillesCreees} famille(s) créée(s), {resultat.ignores} déjà connu(s), {resultat.erreurs} erreur(s).
        </Alert>
      )}
      <div className="flex justify-end gap-2">
        {resultat ? (
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void onDone()}>Terminer</Button>
        ) : (
          <>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
            <Button type="button" variant="petite_enfance" size="sm" disabled={saving || !contenu.trim()} onClick={() => void lancer()}>
              {saving ? 'Import…' : 'Importer'}
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ── Contrats ─────────────────────────────────────────────────────────────────


const CONTRAT_TYPE_LABEL: Record<PeContratType, string> = {
  regulier: 'Régulier', occasionnel: 'Occasionnel', urgence: 'Urgence',
};
const JOURS = [
  { v: 1, l: 'L' }, { v: 2, l: 'M' }, { v: 3, l: 'M' }, { v: 4, l: 'J' }, { v: 5, l: 'V' },
];

function Contrats({
  structures, dossiers, setError,
}: {
  structures: PeStructure[];
  dossiers: PeDossier[];
  setError: (s: string | null) => void;
}) {
  const [contrats, setContrats] = useState<PeContrat[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.petiteEnfance.contrats.list();
      setContrats(r.contrats);
    } catch { setError('Impossible de charger les contrats.'); }
    finally { setLoading(false); }
  }, [setError]);
  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-ink-muted">{contrats.length} contrat(s)</p>
        <Button type="button" variant="petite_enfance" size="sm" onClick={() => setShow(true)}
          disabled={structures.length === 0 || dossiers.length === 0}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nouveau contrat
        </Button>
      </div>
      {structures.length === 0 || dossiers.length === 0 ? (
        <Alert variant="info" icon={AlertCircle} className="mb-3">
          Créez d'abord une structure et au moins un dossier enfant pour établir un contrat.
        </Alert>
      ) : null}

      {loading ? (
        <Card variant="glass" padded><div className="py-6 text-center text-sm text-ink-subtle">Chargement…</div></Card>
      ) : contrats.length === 0 ? (
        <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Aucun contrat d'accueil.</p></Card>
      ) : (
        <div className="space-y-2">
          {contrats.map((c) => (
            <Card key={c.id} variant="glass" className={`p-3.5 ${c.statut === 'clos' ? 'opacity-60' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink">{c.enfantPrenom} {c.enfantNom}</span>
                    <Badge variant="neutral" size="sm">{CONTRAT_TYPE_LABEL[c.type]}</Badge>
                    {c.statut === 'clos' && <Badge variant="neutral" size="sm">Clos</Badge>}
                    {c.statut === 'suspendu' && <Badge variant="warning" size="sm">Suspendu</Badge>}
                  </div>
                  <div className="mt-1 text-[11px] text-ink-subtle">
                    {c.structureNom} · dès le {new Date(c.dateDebut + 'T00:00:00').toLocaleDateString('fr-FR')}
                    {c.dateFin ? ` au ${new Date(c.dateFin + 'T00:00:00').toLocaleDateString('fr-FR')}` : ''}
                    {c.type === 'regulier' && c.joursSemaine.length > 0
                      ? ` · ${c.joursSemaine.map((j) => JOURS.find((x) => x.v === j)?.l ?? j).join('')}`
                      : ''}
                    {c.heuresMensualisees > 0 ? ` · ${c.heuresMensualisees}h/mois` : ''}
                    {c.tarifHoraireEuros > 0 ? ` · ${c.tarifHoraireEuros.toFixed(2)} €/h` : ''}
                  </div>
                </div>
                {c.statut === 'actif' && (
                  <button type="button"
                    onClick={() => api.petiteEnfance.contrats.update(c.id, { statut: 'clos' }).then(load).catch(() => setError('Action impossible.'))}
                    className="flex-shrink-0 text-xs text-ink-muted underline hover:text-ink">Clôturer</button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {show && (
        <ContratForm structures={structures} dossiers={dossiers}
          onClose={() => setShow(false)}
          onSaved={async () => { setShow(false); await load(); }}
          setError={setError} />
      )}
    </>
  );
}

function ContratForm({
  structures, dossiers, onClose, onSaved, setError,
}: {
  structures: PeStructure[]; dossiers: PeDossier[];
  onClose: () => void; onSaved: () => Promise<void>; setError: (s: string | null) => void;
}) {
  const [enfantId, setEnfantId] = useState(dossiers[0]?.enfantId ?? '');
  const [structureId, setStructureId] = useState(structures[0]?.id ?? '');
  const [type, setType] = useState<PeContratType>('regulier');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [jours, setJours] = useState<number[]>([1, 2, 4, 5]);
  const [heures, setHeures] = useState('');
  const [tarif, setTarif] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleJour = (v: number) => setJours((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v].sort());

  const submit = async () => {
    if (!enfantId || !structureId || !dateDebut) return;
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.contrats.create({
        enfantId, structureId, type, dateDebut,
        dateFin: dateFin || null,
        joursSemaine: type === 'regulier' ? jours : [],
        heuresMensualisees: parseFloat(heures.replace(',', '.')) || 0,
        tarifHoraireEuros: parseFloat(tarif.replace(',', '.')) || 0,
      });
      await onSaved();
    } catch { setError('Le contrat n\'a pas pu être créé (vérifiez les dates).'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} title="Nouveau contrat d'accueil" size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="ct-enf">Enfant</label>
            <select id="ct-enf" value={enfantId} onChange={(e) => setEnfantId(e.target.value)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              {dossiers.map((d) => <option key={d.enfantId} value={d.enfantId}>{d.enfantPrenom} {d.enfantNom}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="ct-str">Structure</label>
            <select id="ct-str" value={structureId} onChange={(e) => setStructureId(e.target.value)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              {structures.filter((s) => s.actif).map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="ct-type">Type d'accueil</label>
            <select id="ct-type" value={type} onChange={(e) => setType(e.target.value as PeContratType)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              <option value="regulier">Régulier (mensualisé)</option>
              <option value="occasionnel">Occasionnel</option>
              <option value="urgence">Urgence</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="ct-deb">Début</label>
              <input id="ct-deb" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)}
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-2 text-sm text-ink focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="ct-fin">Fin</label>
              <input id="ct-fin" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)}
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-2 text-sm text-ink focus:outline-none" />
            </div>
          </div>
        </div>

        {type === 'regulier' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">Jours réservés</label>
            <div className="flex gap-1.5">
              {JOURS.map((j) => (
                <button key={j.v} type="button" onClick={() => toggleJour(j.v)}
                  className={`h-9 w-9 rounded-r-md border text-sm font-medium transition ${
                    jours.includes(j.v) ? 'text-white' : 'border-border text-ink-muted'
                  }`}
                  style={jours.includes(j.v) ? { background: ACCENT, borderColor: 'transparent' } : undefined}>
                  {j.l}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="ct-h">Heures / mois</label>
            <input id="ct-h" value={heures} onChange={(e) => setHeures(e.target.value)} inputMode="decimal" placeholder="140"
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="ct-t">Tarif horaire (€)</label>
            <input id="ct-t" value={tarif} onChange={(e) => setTarif(e.target.value)} inputMode="decimal" placeholder="1,50"
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
        </div>
        <p className="text-[11px] text-ink-subtle">Le tarif PSU (barème CAF) sera calculé automatiquement au lot facturation ; ce tarif horaire sert de repli.</p>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void submit()} disabled={saving || !enfantId || !structureId || !dateDebut}>
            {saving ? 'Enregistrement…' : 'Créer le contrat'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Présences & encadrement ──────────────────────────────────────────────────


function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function PresencesEncadrement({
  structures, dossiers, setError,
}: {
  structures: PeStructure[];
  dossiers: PeDossier[];
  setError: (s: string | null) => void;
}) {
  const actives = structures.filter((s) => s.actif);
  const [structureId, setStructureId] = useState(actives[0]?.id ?? '');
  const [date, setDate] = useState(todayIso());
  const [presences, setPresences] = useState<PePresence[]>([]);
  const [enc, setEnc] = useState<PeEncadrementJour | null>(null);
  const [planning, setPlanning] = useState<PePlanningCreneau[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!structureId || !date) return;
    setLoading(true);
    try {
      const [p, e, pl] = await Promise.all([
        api.petiteEnfance.presences.list(structureId, date),
        api.petiteEnfance.encadrement.get(structureId, date),
        api.petiteEnfance.planning.list(`${date}T00:00:00Z`, `${date}T23:59:59Z`, structureId),
      ]);
      setPresences(p.presences);
      setEnc(e);
      setPlanning(pl.creneaux);
    } catch { setError('Impossible de charger les présences.'); }
    finally { setLoading(false); }
  }, [structureId, date, setError]);
  useEffect(() => { void load(); }, [load]);

  if (actives.length === 0) {
    return <Alert variant="info" icon={AlertCircle}>Créez d'abord une structure d'accueil.</Alert>;
  }

  // Enfants pointables : ceux ayant un dossier (inscrits). Statut courant depuis presences.
  const presenceByEnfant = new Map(presences.map((p) => [p.enfantId, p]));
  const structureActive = actives.find((s) => s.id === structureId);
  const ouverture = structureActive?.amplitudeDebut ?? '08:00';
  const fermeture = structureActive?.amplitudeFin ?? '18:00';

  // Horodate une arrivée ou un départ (heures calées sur l'amplitude de l'établissement).
  const horodater = async (enfantId: string, sens: 'arrivee' | 'depart', heure: string) => {
    const p = presenceByEnfant.get(enfantId);
    try {
      await api.petiteEnfance.presences.upsert({
        enfantId, structureId, dateJour: date,
        arrivee: sens === 'arrivee' ? heure : (p?.arrivee ?? null),
        depart: sens === 'depart' ? heure : (p?.depart ?? null),
        absent: false,
      });
      await load();
    } catch { setError('Pointage impossible.'); }
  };

  const marquerAbsent = async (enfantId: string) => {
    try {
      await api.petiteEnfance.presences.upsert({ enfantId, structureId, dateJour: date, arrivee: null, depart: null, absent: true });
      await load();
    } catch { setError('Pointage impossible.'); }
  };

  return (
    <>
      {/* Sélecteurs */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="pe-str">Structure</label>
          <select id="pe-str" value={structureId} onChange={(e) => setStructureId(e.target.value)}
            className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-1.5 text-sm text-ink focus:outline-none">
            {actives.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="pe-date">Jour</label>
          <input id="pe-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-1.5 text-sm text-ink focus:outline-none" />
        </div>
        <Button type="button" variant="secondary" size="sm" disabled={!structureId || !date} onClick={() => {
          void (async () => {
            try {
              const blob = await api.petiteEnfance.presences.feuillePdfBlob(structureId, date);
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `pointage-petite-enfance-${date}.pdf`; a.click();
              URL.revokeObjectURL(url);
            } catch { setError('Export PDF indisponible.'); }
          })();
        }}>
          Feuille de pointage PDF
        </Button>
      </div>

      {/* Bandeau encadrement + 2 taux côte à côte */}
      {enc && (
        <Card variant="glass" className={`mb-4 p-4 ${enc.conforme ? '' : 'border-urgent/40'}`}>
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <div className="text-xs text-ink-subtle">Encadrement (planning)</div>
              <div className="text-lg font-semibold text-ink">{enc.nbEncadrants} encadrant(s)</div>
              <div className="text-[11px] text-ink-subtle">{enc.nbEnfantsPresents} présent(s) · {enc.nbMarchent} marchent / {enc.nbNeMarchentPas} non</div>
            </div>
            <div className="h-10 w-px bg-border" />
            <div>
              <div className="text-xs text-ink-subtle">Taux d'occupation</div>
              <div className="text-lg font-semibold text-ink">{enc.tauxOccupation}%</div>
              <div className="text-[11px] text-ink-subtle">{enc.nbEnfantsPresents} / {enc.placesAgreees} places</div>
            </div>
            <div className="h-10 w-px bg-border" />
            <div>
              <div className="text-xs text-ink-subtle">Occupation / capacité PMI</div>
              <div className="text-lg font-semibold text-ink">{enc.tauxOccupationPmi}%</div>
              <div className="text-[11px] text-ink-subtle">capacité encadrants : {enc.capaciteReglementaire}</div>
            </div>
            <div className="ml-auto">
              {enc.conforme
                ? <Badge variant="success" size="sm">Taux d'encadrement conforme</Badge>
                : <Badge variant="urgent" size="sm">Encadrement insuffisant (PMI)</Badge>}
            </div>
          </div>
          {planning.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border/60 pt-3">
              <span className="text-[11px] text-ink-subtle">Planifiés :</span>
              {planning.map((c) => (
                <span key={c.id} className={`rounded-r-sm px-1.5 py-0.5 text-[10px] ${c.statut === 'absent' ? 'bg-urgent/10 text-urgent line-through' : 'bg-surface/60 text-ink-muted'}`}>
                  {c.agentNom}
                </span>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Pointage des enfants */}
      {loading ? (
        <Card variant="glass" padded><div className="py-6 text-center text-sm text-ink-subtle">Chargement…</div></Card>
      ) : dossiers.length === 0 ? (
        <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Aucun enfant inscrit à pointer.</p></Card>
      ) : (
        <div className="space-y-1.5">
          {dossiers.map((d) => {
            const p = presenceByEnfant.get(d.enfantId);
            const present = p ? !p.absent : false;
            const pointe = !!p;
            const arrivee = p?.arrivee ?? '';
            const depart = p?.depart ?? '';
            return (
              <Card key={d.enfantId} variant="glass" className="p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">{d.enfantPrenom} {d.enfantNom}</div>
                    <div className="text-[11px] text-ink-subtle">
                      {d.marche ? 'Marche' : 'Ne marche pas'}
                      {pointe && present && p?.minutesRealisees ? ` · ${Math.floor(p.minutesRealisees / 60)}h${String(p.minutesRealisees % 60).padStart(2, '0')} de présence` : ''}
                    </div>
                  </div>
                  {pointe && !present && <span className="shrink-0 text-xs font-medium text-danger">Absent</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Arrivée : bouton (défaut = ouverture de l'établissement) + heure */}
                  <button type="button" onClick={() => void horodater(d.enfantId, 'arrivee', arrivee || ouverture)}
                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                      arrivee ? 'text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}
                    style={arrivee ? { background: 'oklch(0.62 0.16 155)' } : undefined}>
                    Arrivée{arrivee ? ' ✓' : ''}
                  </button>
                  <input type="time" value={arrivee} aria-label="Heure d'arrivée"
                    onChange={(e) => { if (e.target.value) void horodater(d.enfantId, 'arrivee', e.target.value); }}
                    className="h-8 rounded-r-md border border-border bg-surface px-2 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petite_enfance" />
                  <span className="text-ink-subtle">→</span>
                  {/* Départ : bouton (défaut = fermeture / question du midi) + heure */}
                  <button type="button" onClick={() => void horodater(d.enfantId, 'depart', depart || fermeture)}
                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                      depart ? 'text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}
                    style={depart ? { background: 'oklch(0.62 0.18 25)' } : undefined}>
                    Départ{depart ? ' ✓' : ''}
                  </button>
                  <input type="time" value={depart} aria-label="Heure de départ"
                    onChange={(e) => { if (e.target.value) void horodater(d.enfantId, 'depart', e.target.value); }}
                    className="h-8 rounded-r-md border border-border bg-surface px-2 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petite_enfance" />
                  {/* Repère midi : bouton rapide "départ 12h" pour savoir s'il reste ou non */}
                  <button type="button" onClick={() => void horodater(d.enfantId, 'depart', '12:00')}
                    className="inline-flex h-8 items-center rounded-r-pill border border-border px-3 text-xs font-medium text-ink-muted transition-all hover:bg-surface/60">
                    Départ 12h
                  </button>
                  <button type="button" onClick={() => void marquerAbsent(d.enfantId)}
                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                      pointe && !present ? 'text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}
                    style={pointe && !present ? { background: 'oklch(0.62 0.18 25)' } : undefined}>
                    Absent
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Facturation ──────────────────────────────────────────────────────────────

function moisCourant(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Facturation({
  structures, setError,
}: {
  structures: PeStructure[];
  setError: (s: string | null) => void;
}) {
  const actives = structures.filter((s) => s.actif);
  const [params, setParams] = useState<PeParametres | null>(null);
  const [structureId, setStructureId] = useState(actives[0]?.id ?? '');
  const [periode, setPeriode] = useState(moisCourant());
  const [apercu, setApercu] = useState<PeFactureApercu | null>(null);
  const [factures, setFactures] = useState<PeFacture[]>([]);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [showParams, setShowParams] = useState(false);
  const { user } = useAuth();
  // Paramètres PSU + barème = config réservée admin/maire (garde-fou backend requireRole).
  const isAdmin = user ? ['super_admin', 'admin'].includes(user.role) : false;

  const loadParams = useCallback(async () => {
    try { setParams(await api.petiteEnfance.facturation.parametres()); } catch { /* */ }
  }, []);
  const loadFactures = useCallback(async () => {
    try { setFactures((await api.petiteEnfance.facturation.factures()).factures); } catch { /* */ }
  }, []);
  useEffect(() => { void loadParams(); void loadFactures(); }, [loadParams, loadFactures]);

  const loadApercu = useCallback(async () => {
    if (!structureId || !periode) return;
    setLoading(true);
    try { setApercu(await api.petiteEnfance.facturation.apercu(structureId, periode)); }
    catch { setError('Aperçu impossible.'); }
    finally { setLoading(false); }
  }, [structureId, periode, setError]);
  useEffect(() => { void loadApercu(); }, [loadApercu]);

  const emettre = async () => {
    setEmitting(true); setError(null);
    try {
      const r = await api.petiteEnfance.facturation.emettre(structureId, periode);
      await loadFactures();
      if (r.creees === 0) setError('Aucune nouvelle facture (déjà émises ou rien à facturer).');
    } catch { setError('Émission impossible.'); }
    finally { setEmitting(false); }
  };

  if (actives.length === 0) {
    return <Alert variant="info" icon={AlertCircle}>Créez d'abord une structure d'accueil.</Alert>;
  }

  return (
    <>
      {/* Mode de facturation */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Badge variant={params?.psuEnabled ? 'info' : 'neutral'} size="sm">
          {params?.psuEnabled ? 'Barème PSU (CAF)' : 'Tarif simple'}
        </Badge>
        {isAdmin && (
          <button type="button" onClick={() => setShowParams(true)} className="text-xs text-ink-muted underline hover:text-ink">
            Paramètres de facturation
          </button>
        )}
      </div>

      {/* Sélecteurs + émission */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="fa-str">Structure</label>
          <select id="fa-str" value={structureId} onChange={(e) => setStructureId(e.target.value)}
            className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-1.5 text-sm text-ink focus:outline-none">
            {actives.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="fa-per">Mois</label>
          <input id="fa-per" type="month" value={periode} onChange={(e) => setPeriode(e.target.value)}
            className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-1.5 text-sm text-ink focus:outline-none" />
        </div>
        <Button type="button" variant="petite_enfance" size="sm" onClick={() => void emettre()}
          disabled={emitting || !apercu || apercu.lignes.length === 0}>
          {emitting ? 'Émission…' : 'Émettre les factures du mois'}
        </Button>
      </div>

      {/* Aperçu */}
      {loading ? (
        <Card variant="glass" padded><div className="py-6 text-center text-sm text-ink-subtle">Calcul…</div></Card>
      ) : apercu && apercu.lignes.length > 0 ? (
        <Card variant="glass" className="mb-5 overflow-hidden p-0">
          <div className="flex items-center justify-between border-b border-border bg-surface/30 px-4 py-2.5">
            <span className="text-sm font-medium text-ink">Aperçu — {apercu.lignes.length} enfant(s)</span>
            <span className="text-sm font-semibold text-ink">{apercu.totalEuros.toFixed(2)} €</span>
          </div>
          <div className="divide-y divide-border/60">
            {apercu.lignes.map((l) => (
              <div key={l.contratId} className="flex items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink">{l.enfantPrenom} {l.enfantNom}</div>
                  <div className="text-[11px] text-ink-subtle">{l.detail}</div>
                </div>
                <Badge variant="neutral" size="sm">
                  {l.mode === 'psu' ? 'PSU' : l.mode === 'mensualise' ? 'Mensualisé' : 'Réalisé'}
                </Badge>
                <span className="w-20 text-right text-sm font-semibold tabular-nums text-ink">{l.montantEuros.toFixed(2)} €</span>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Rien à facturer sur ce mois (contrats actifs requis).</p></Card>
      )}

      {/* Factures émises */}
      <div className="mb-2 font-display text-sm font-semibold text-ink">Factures émises</div>
      {factures.length === 0 ? (
        <Card variant="glass" padded><p className="py-4 text-center text-sm text-ink-subtle">Aucune facture émise.</p></Card>
      ) : (
        <div className="space-y-1.5">
          {factures.map((f) => (
            <Card key={f.id} variant="glass" className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs text-ink">{f.numero}</div>
                <div className="text-[11px] text-ink-subtle">
                  {new Date(f.periodeDebut + 'T00:00:00').toLocaleDateString('fr-FR')} → {new Date(f.periodeFin + 'T00:00:00').toLocaleDateString('fr-FR')} · émise le {new Date(f.dateEmission + 'T00:00:00').toLocaleDateString('fr-FR')}
                </div>
              </div>
              <Badge variant={f.statut === 'payee' ? 'success' : f.statut === 'impayee' ? 'urgent' : 'neutral'} size="sm">{f.statut}</Badge>
              <span className="text-sm font-semibold tabular-nums text-ink">{f.montantEuros.toFixed(2)} €</span>
              <button type="button" title="Télécharger (PDF)"
                onClick={() => downloadBlob(() => api.petiteEnfance.factureBlob(f.id), `${f.numero}.pdf`).catch(() => setError('Téléchargement impossible.'))}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-r-md border border-border text-ink-muted transition hover:bg-surface hover:text-ink">
                <FileText className="h-4 w-4" aria-hidden="true" />
              </button>
              <button type="button" title="Envoyer par e-mail"
                onClick={() => api.petiteEnfance.factureEmail(f.id).then((r) => { if (!r.sent) setError('E-mail non envoyé : ' + (r.reason ?? 'adresse manquante')); }).catch(() => setError('Envoi impossible.'))}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-r-md border border-border text-ink-muted transition hover:bg-surface hover:text-ink">
                <Mail className="h-4 w-4" aria-hidden="true" />
              </button>
            </Card>
          ))}
        </div>
      )}

      {showParams && <ParametresFacturation params={params} onClose={() => setShowParams(false)}
        onSaved={async () => { setShowParams(false); await loadParams(); await loadApercu(); }} setError={setError} />}
    </>
  );
}

function ParametresFacturation({
  params, onClose, onSaved, setError,
}: {
  params: PeParametres | null;
  onClose: () => void; onSaved: () => Promise<void>; setError: (s: string | null) => void;
}) {
  const [psu, setPsu] = useState(params?.psuEnabled ?? false);
  const [tarifRepli, setTarifRepli] = useState(params?.tarifHoraireDefautEuros ? String(params.tarifHoraireDefautEuros) : '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.facturation.setParametres({
        psuEnabled: psu,
        tarifHoraireDefautEuros: parseFloat(tarifRepli.replace(',', '.')) || 0,
      });
      await onSaved();
    } catch { setError('Enregistrement impossible.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} title="Paramètres de facturation" size="md">
      <div className="space-y-4">
        <label className="flex items-center gap-2.5">
          <input type="checkbox" checked={psu} onChange={(e) => setPsu(e.target.checked)} className="h-4 w-4" />
          <span className="text-sm text-ink">Utiliser le barème PSU (CAF)</span>
        </label>
        <p className="text-[11px] text-ink-subtle">
          Si activé, le tarif horaire est calculé selon les ressources de la famille et le taux d'effort
          (barème à renseigner par année). Sinon, le tarif horaire du contrat s'applique (tarif simple).
        </p>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="pa-repli">Tarif horaire de repli (€)</label>
          <input id="pa-repli" value={tarifRepli} onChange={(e) => setTarifRepli(e.target.value)} inputMode="decimal" placeholder="1,50"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <p className="mt-1 text-[11px] text-ink-subtle">Utilisé si un contrat n'a pas de tarif renseigné.</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void save()} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── RAM-RPE ──────────────────────────────────────────────────────────────────

function Ram({ setError }: { setError: (s: string | null) => void }) {
  const [sub, setSub] = useState<'assmat' | 'ateliers'>('assmat');
  const [assmat, setAssmat] = useState<PeAssmat[]>([]);
  const [ateliers, setAteliers] = useState<PeAtelier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAssmat, setShowAssmat] = useState(false);
  const [showAtelier, setShowAtelier] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, at] = await Promise.all([api.petiteEnfance.ram.assmat(), api.petiteEnfance.ram.ateliers()]);
      setAssmat(a.assmat);
      setAteliers(at.ateliers);
    } catch { setError('Impossible de charger le RAM.'); }
    finally { setLoading(false); }
  }, [setError]);
  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-2">
          {([['assmat', 'Assistantes maternelles'], ['ateliers', 'Ateliers']] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setSub(k)}
              className={`rounded-r-pill border px-3 py-1.5 text-xs font-medium transition ${sub === k ? 'text-white' : 'border-border text-ink-muted hover:bg-surface/40'}`}
              style={sub === k ? { background: ACCENT, borderColor: 'transparent' } : undefined}>
              {l}
            </button>
          ))}
        </div>
        {sub === 'assmat'
          ? <Button type="button" variant="petite_enfance" size="sm" onClick={() => setShowAssmat(true)}><Plus className="h-4 w-4" aria-hidden="true" />Assistante maternelle</Button>
          : <Button type="button" variant="petite_enfance" size="sm" onClick={() => setShowAtelier(true)}><Plus className="h-4 w-4" aria-hidden="true" />Atelier</Button>}
      </div>

      {loading ? (
        <Card variant="glass" padded><div className="py-6 text-center text-sm text-ink-subtle">Chargement…</div></Card>
      ) : sub === 'assmat' ? (
        <AssmatList assmat={assmat} onChange={load} setError={setError} />
      ) : (
        <AteliersList ateliers={ateliers} assmat={assmat} onChange={load} setError={setError} />
      )}

      {showAssmat && <AssmatForm onClose={() => setShowAssmat(false)} onSaved={async () => { setShowAssmat(false); await load(); }} setError={setError} />}
      {showAtelier && <AtelierForm onClose={() => setShowAtelier(false)} onSaved={async () => { setShowAtelier(false); await load(); }} setError={setError} />}
    </>
  );
}

function AssmatList({ assmat, onChange, setError }: { assmat: PeAssmat[]; onChange: () => Promise<void>; setError: (s: string | null) => void }) {
  const toggleVisible = async (a: PeAssmat) => {
    try { await api.petiteEnfance.ram.updateAssmat(a.id, { visibleAnnuaire: !a.visibleAnnuaire }); await onChange(); }
    catch { setError('Modification impossible.'); }
  };
  if (assmat.length === 0) return <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Aucune assistante maternelle.</p></Card>;
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {assmat.map((a) => (
        <Card key={a.id} variant="glass" className={`p-3.5 ${a.actif ? '' : 'opacity-60'}`}>
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="font-medium text-ink">{a.prenom} {a.nom}</div>
              <div className="text-[11px] text-ink-subtle">
                {a.commune ?? '—'}{a.telephone ? ` · ${a.telephone}` : ''}
              </div>
            </div>
            {a.placesDispo > 0
              ? <Badge variant="success" size="sm">{a.placesDispo} place(s) libre(s)</Badge>
              : <Badge variant="neutral" size="sm">Complet</Badge>}
          </div>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-subtle">
            <span>{a.placesDispo}/{a.placesAgreees} dispo</span>
            <button type="button" onClick={() => void toggleVisible(a)} className="underline hover:text-ink">
              {a.visibleAnnuaire ? 'Visible annuaire ✓' : 'Masquée de l\'annuaire'}
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function AteliersList({ ateliers, assmat, onChange, setError }: { ateliers: PeAtelier[]; assmat: PeAssmat[]; onChange: () => Promise<void>; setError: (s: string | null) => void }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [inscriptions, setInscriptions] = useState<PeAtelierInscription[]>([]);

  const loadInsc = async (id: string) => {
    setOpenId(id);
    try { setInscriptions((await api.petiteEnfance.ram.inscriptions(id)).inscriptions); } catch { setError('Chargement impossible.'); }
  };
  const inscrire = async (atelierId: string, assmatId: string) => {
    try { await api.petiteEnfance.ram.inscrire(atelierId, { assmatId, nbEnfants: 1 }); await loadInsc(atelierId); await onChange(); }
    catch { setError('Inscription impossible (atelier complet ou fermé ?).'); }
  };

  if (ateliers.length === 0) return <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Aucun atelier programmé.</p></Card>;
  return (
    <div className="space-y-2">
      {ateliers.map((a) => (
        <Card key={a.id} variant="glass" className="p-3.5">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-ink">{a.titre}</span>
                {a.statut === 'annule' && <Badge variant="urgent" size="sm">Annulé</Badge>}
                {a.statut === 'complet' && <Badge variant="warning" size="sm">Complet</Badge>}
              </div>
              <div className="text-[11px] text-ink-subtle">
                {new Date(a.dateAtelier + 'T00:00:00').toLocaleDateString('fr-FR')}
                {a.heureDebut ? ` · ${a.heureDebut}${a.heureFin ? '–' + a.heureFin : ''}` : ''}
                {a.lieu ? ` · ${a.lieu}` : ''}
                {` · ${a.nbInscrits}${a.places > 0 ? '/' + a.places : ''} inscrit(s)`}
              </div>
            </div>
            <button type="button" onClick={() => (openId === a.id ? setOpenId(null) : void loadInsc(a.id))}
              className="flex-shrink-0 text-xs text-ink-muted underline hover:text-ink">
              {openId === a.id ? 'Fermer' : 'Inscriptions'}
            </button>
          </div>
          {openId === a.id && (
            <div className="mt-3 border-t border-border/60 pt-3">
              {inscriptions.length === 0
                ? <p className="text-[11px] text-ink-subtle">Aucune inscription.</p>
                : <div className="mb-2 flex flex-wrap gap-1.5">
                    {inscriptions.map((i) => (
                      <span key={i.id} className="rounded-r-sm bg-surface/60 px-1.5 py-0.5 text-[10px] text-ink-muted">
                        {i.assmatNom ?? i.enfantNom ?? '—'} ({i.nbEnfants})
                      </span>
                    ))}
                  </div>}
              {a.statut === 'ouvert' && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-ink-subtle">Inscrire :</span>
                  {assmat.filter((m) => m.actif).map((m) => (
                    <button key={m.id} type="button" onClick={() => void inscrire(a.id, m.id)}
                      className="rounded-r-sm border border-border px-1.5 py-0.5 text-[10px] text-ink-muted hover:bg-surface">
                      {m.prenom} {m.nom}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function AssmatForm({ onClose, onSaved, setError }: { onClose: () => void; onSaved: () => Promise<void>; setError: (s: string | null) => void }) {
  const [f, setF] = useState({ prenom: '', nom: '', telephone: '', commune: '', placesAgreees: '', placesDispo: '', visibleAnnuaire: true });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.prenom.trim() || !f.nom.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.ram.createAssmat({
        prenom: f.prenom.trim(), nom: f.nom.trim(), telephone: f.telephone || null, commune: f.commune || null,
        placesAgreees: parseInt(f.placesAgreees, 10) || 0, placesDispo: parseInt(f.placesDispo, 10) || 0,
        visibleAnnuaire: f.visibleAnnuaire,
      });
      await onSaved();
    } catch { setError('Création impossible (places dispo ≤ agréées ?).'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} title="Nouvelle assistante maternelle" size="md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })} placeholder="Prénom" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} placeholder="Nom" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} placeholder="Téléphone" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input value={f.commune} onChange={(e) => setF({ ...f, commune: e.target.value })} placeholder="Commune" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input value={f.placesAgreees} onChange={(e) => setF({ ...f, placesAgreees: e.target.value })} inputMode="numeric" placeholder="Places agréées" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input value={f.placesDispo} onChange={(e) => setF({ ...f, placesDispo: e.target.value })} inputMode="numeric" placeholder="Places dispo" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={f.visibleAnnuaire} onChange={(e) => setF({ ...f, visibleAnnuaire: e.target.checked })} className="h-4 w-4" />
          Visible dans l'annuaire des parents
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void submit()} disabled={saving || !f.prenom.trim() || !f.nom.trim()}>{saving ? 'Enregistrement…' : 'Créer'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

function AtelierForm({ onClose, onSaved, setError }: { onClose: () => void; onSaved: () => Promise<void>; setError: (s: string | null) => void }) {
  const [f, setF] = useState({ titre: '', dateAtelier: '', heureDebut: '10:00', heureFin: '11:00', lieu: '', places: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.titre.trim() || !f.dateAtelier) return;
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.ram.createAtelier({
        titre: f.titre.trim(), dateAtelier: f.dateAtelier, heureDebut: f.heureDebut || null, heureFin: f.heureFin || null,
        lieu: f.lieu || null, places: parseInt(f.places, 10) || 0,
      });
      await onSaved();
    } catch { setError('Création impossible.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} title="Nouvel atelier d'éveil" size="md">
      <div className="space-y-3">
        <input value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })} placeholder="Titre (ex : éveil musical)" className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="at-date">Date</label>
            <input id="at-date" type="date" value={f.dateAtelier} onChange={(e) => setF({ ...f, dateAtelier: e.target.value })} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="at-pl">Places (0 = illimité)</label>
            <input id="at-pl" value={f.places} onChange={(e) => setF({ ...f, places: e.target.value })} inputMode="numeric" placeholder="0" className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <input type="time" value={f.heureDebut} onChange={(e) => setF({ ...f, heureDebut: e.target.value })} className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input type="time" value={f.heureFin} onChange={(e) => setF({ ...f, heureFin: e.target.value })} className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
        </div>
        <input value={f.lieu} onChange={(e) => setF({ ...f, lieu: e.target.value })} placeholder="Lieu" className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void submit()} disabled={saving || !f.titre.trim() || !f.dateAtelier}>{saving ? 'Enregistrement…' : 'Créer'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Pré-inscriptions / liste d'attente / commission ──────────────────────────


const PREINSC_STATUT: Record<PePreinscStatut, { label: string; variant: 'neutral' | 'info' | 'success' | 'warning' | 'urgent' }> = {
  deposee: { label: 'Déposée', variant: 'neutral' },
  en_attente: { label: 'En attente', variant: 'info' },
  proposee: { label: 'Place proposée', variant: 'warning' },
  acceptee: { label: 'Acceptée', variant: 'success' },
  refusee: { label: 'Refusée', variant: 'urgent' },
  annulee: { label: 'Annulée', variant: 'neutral' },
};

function Preinscriptions({ structures, setError }: { structures: PeStructure[]; setError: (s: string | null) => void }) {
  const [items, setItems] = useState<PePreinscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [attribuer, setAttribuer] = useState<PePreinscription | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.petiteEnfance.preinscriptions.list()).preinscriptions); }
    catch { setError('Impossible de charger les pré-inscriptions.'); }
    finally { setLoading(false); }
  }, [setError]);
  useEffect(() => { void load(); }, [load]);

  const setStatut = async (id: string, statut: PePreinscStatut) => {
    try { await api.petiteEnfance.preinscriptions.setStatut(id, statut); await load(); }
    catch { setError('Action impossible.'); }
  };

  const enAttente = items.filter((i) => i.statut !== 'acceptee' && i.statut !== 'refusee' && i.statut !== 'annulee');
  const traitees = items.filter((i) => i.statut === 'acceptee' || i.statut === 'refusee' || i.statut === 'annulee');

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-ink-muted">{enAttente.length} demande(s) en liste d'attente</p>
        <Button type="button" variant="petite_enfance" size="sm" onClick={() => setShow(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nouvelle pré-inscription
        </Button>
      </div>

      {loading ? (
        <Card variant="glass" padded><div className="py-6 text-center text-sm text-ink-subtle">Chargement…</div></Card>
      ) : (
        <>
          {/* Liste d'attente (triée par score) */}
          {enAttente.length === 0 ? (
            <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Aucune demande en attente.</p></Card>
          ) : (
            <div className="space-y-2">
              {enAttente.map((p, idx) => (
                <Card key={p.id} variant="glass" className="p-3.5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: ACCENT }}>
                      {idx + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink">{p.enfantPrenom} {p.enfantNom}</span>
                        <Badge variant={PREINSC_STATUT[p.statut].variant} size="sm">{PREINSC_STATUT[p.statut].label}</Badge>
                        {p.typeSouhaite === 'urgence' && <Badge variant="urgent" size="sm">Urgence</Badge>}
                      </div>
                      <div className="text-[11px] text-ink-subtle">
                        Déposée le {new Date(p.dateDepot + 'T00:00:00').toLocaleDateString('fr-FR')}
                        {p.structureNom ? ` · ${p.structureNom}` : ''}
                        {p.dateAccueilSouhaitee ? ` · souhait : ${new Date(p.dateAccueilSouhaitee + 'T00:00:00').toLocaleDateString('fr-FR')}` : ''}
                        {p.heuresSouhaitees > 0 ? ` · ${p.heuresSouhaitees} h/mois souhaitées` : ''}
                        {[p.communeResidence && 'commune', p.fratrieAccueillie && 'fratrie', p.parentActif && 'parent actif'].filter(Boolean).length > 0
                          ? ` · ${[p.communeResidence && 'commune', p.fratrieAccueillie && 'fratrie', p.parentActif && 'parent actif'].filter(Boolean).join(', ')}` : ''}
                      </div>
                      {p.destinataire && (
                        <div className="mt-0.5 text-[11px] text-ink-subtle">Proposition à : <span className="text-ink">{p.destinataire}</span></div>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <span className="rounded-r-sm bg-surface/60 px-2 py-1 text-[11px] font-semibold text-ink" title="Score de priorité">{p.score} pts</span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 border-t border-border/60 pt-2">
                    {p.statut !== 'proposee' && (
                      <button type="button" onClick={() => void setStatut(p.id, 'proposee')} className="text-xs text-ink-muted underline hover:text-ink">Proposer une place</button>
                    )}
                    <button type="button" onClick={() => setAttribuer(p)} className="text-xs font-medium underline" style={{ color: ACCENT }}>Attribuer (→ contrat)</button>
                    <button type="button" onClick={() => void setStatut(p.id, 'refusee')} className="text-xs text-ink-muted underline hover:text-ink">Refuser</button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Traitées */}
          {traitees.length > 0 && (
            <div className="mt-5">
              <div className="mb-2 font-display text-sm font-semibold text-ink">Traitées</div>
              <div className="space-y-1.5">
                {traitees.map((p) => (
                  <Card key={p.id} variant="glass" className="flex items-center gap-3 p-3 opacity-75">
                    <div className="min-w-0 flex-1">
                      <span className="text-sm text-ink">{p.enfantPrenom} {p.enfantNom}</span>
                      {p.contratId && <span className="ml-2 text-[11px] text-ink-subtle">contrat généré</span>}
                    </div>
                    <Badge variant={PREINSC_STATUT[p.statut].variant} size="sm">{PREINSC_STATUT[p.statut].label}</Badge>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {show && <PreinscForm structures={structures} onClose={() => setShow(false)} onSaved={async () => { setShow(false); await load(); }} setError={setError} />}
      {attribuer && <AttribuerForm preinsc={attribuer} structures={structures} onClose={() => setAttribuer(null)} onSaved={async () => { setAttribuer(null); await load(); }} setError={setError} />}
    </>
  );
}

function PreinscForm({ structures, onClose, onSaved, setError }: { structures: PeStructure[]; onClose: () => void; onSaved: () => Promise<void>; setError: (s: string | null) => void }) {
  const [f, setF] = useState({
    enfantPrenom: '', enfantNom: '', dateNaissance: '', ne: false,
    parentNom: '', parentTel: '', structureId: '', dateAccueilSouhaitee: '',
    typeSouhaite: 'regulier' as PeContratType,
    communeResidence: false, fratrieAccueillie: false, parentActif: false, quotientFamilial: '',
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.enfantPrenom.trim() && !f.enfantNom.trim() && !f.parentNom.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.preinscriptions.create({
        enfantPrenom: f.enfantPrenom || null, enfantNom: f.enfantNom || null,
        dateNaissance: f.dateNaissance || null, ne: f.ne,
        parentNom: f.parentNom || null, parentTel: f.parentTel || null,
        structureId: f.structureId || null, dateAccueilSouhaitee: f.dateAccueilSouhaitee || null,
        typeSouhaite: f.typeSouhaite,
        communeResidence: f.communeResidence, fratrieAccueillie: f.fratrieAccueillie, parentActif: f.parentActif,
        quotientFamilial: f.quotientFamilial ? parseInt(f.quotientFamilial, 10) : null,
      });
      await onSaved();
    } catch { setError('Création impossible.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} title="Nouvelle pré-inscription" size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input value={f.enfantPrenom} onChange={(e) => setF({ ...f, enfantPrenom: e.target.value })} placeholder="Prénom enfant" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input value={f.enfantNom} onChange={(e) => setF({ ...f, enfantNom: e.target.value })} placeholder="Nom enfant" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="pi-dn">Naissance (réelle ou prévue)</label>
            <input id="pi-dn" type="date" value={f.dateNaissance} onChange={(e) => setF({ ...f, dateNaissance: e.target.value })} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <label className="flex items-end gap-2 pb-2 text-sm text-ink">
            <input type="checkbox" checked={f.ne} onChange={(e) => setF({ ...f, ne: e.target.checked })} className="h-4 w-4" /> Enfant déjà né
          </label>
          <input value={f.parentNom} onChange={(e) => setF({ ...f, parentNom: e.target.value })} placeholder="Nom du parent" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input value={f.parentTel} onChange={(e) => setF({ ...f, parentTel: e.target.value })} placeholder="Téléphone parent" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="pi-str">Structure souhaitée</label>
            <select id="pi-str" value={f.structureId} onChange={(e) => setF({ ...f, structureId: e.target.value })} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              <option value="">— indifférent —</option>
              {structures.filter((s) => s.actif).map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="pi-type">Type souhaité</label>
            <select id="pi-type" value={f.typeSouhaite} onChange={(e) => setF({ ...f, typeSouhaite: e.target.value as PeContratType })} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              <option value="regulier">Régulier</option>
              <option value="occasionnel">Occasionnel</option>
              <option value="urgence">Urgence</option>
            </select>
          </div>
        </div>
        <div className="rounded-r-md border border-border/60 bg-surface/30 p-3">
          <div className="mb-2 text-[11px] font-medium text-ink-muted">Critères de priorité</div>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-1.5 text-xs text-ink"><input type="checkbox" checked={f.communeResidence} onChange={(e) => setF({ ...f, communeResidence: e.target.checked })} className="h-4 w-4" /> Habite la commune</label>
            <label className="flex items-center gap-1.5 text-xs text-ink"><input type="checkbox" checked={f.fratrieAccueillie} onChange={(e) => setF({ ...f, fratrieAccueillie: e.target.checked })} className="h-4 w-4" /> Fratrie accueillie</label>
            <label className="flex items-center gap-1.5 text-xs text-ink"><input type="checkbox" checked={f.parentActif} onChange={(e) => setF({ ...f, parentActif: e.target.checked })} className="h-4 w-4" /> Parent en activité</label>
            <input value={f.quotientFamilial} onChange={(e) => setF({ ...f, quotientFamilial: e.target.value })} inputMode="numeric" placeholder="QF (optionnel)" className="w-28 rounded-r-md border border-border-hi bg-surface/80 px-2 py-1 text-xs text-ink focus:outline-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void submit()} disabled={saving}>{saving ? 'Enregistrement…' : 'Créer la demande'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

function AttribuerForm({ preinsc, structures, onClose, onSaved, setError }: { preinsc: PePreinscription; structures: PeStructure[]; onClose: () => void; onSaved: () => Promise<void>; setError: (s: string | null) => void }) {
  const [structureId, setStructureId] = useState(preinsc.structureId ?? structures.filter((s) => s.actif)[0]?.id ?? '');
  const [dateDebut, setDateDebut] = useState(preinsc.dateAccueilSouhaitee ?? '');
  // Pré-remplissage : on reprend ce que la famille a demandé → il suffit de valider.
  const [heures, setHeures] = useState(preinsc.heuresSouhaitees > 0 ? String(preinsc.heuresSouhaitees) : '');
  const [tarif, setTarif] = useState(preinsc.tarifHoraireSouhaiteEuros > 0 ? String(preinsc.tarifHoraireSouhaiteEuros) : '');
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!dateDebut) return;
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.preinscriptions.attribuer(preinsc.id, {
        structureId: structureId || undefined, dateDebut,
        heuresMensualisees: parseFloat(heures.replace(',', '.')) || 0,
        tarifHoraireEuros: parseFloat(tarif.replace(',', '.')) || 0,
        motif: motif.trim() || null,
      });
      await onSaved();
    } catch { setError('Attribution impossible. Un enfant (dossier) doit être rattaché à la demande.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} title="Attribuer une place" size="md">
      <div className="space-y-3">
        <p className="text-[11px] text-ink-subtle">
          L'attribution génère un contrat d'accueil pour {preinsc.enfantPrenom} {preinsc.enfantNom} et passe la demande à « acceptée ».
          La demande doit être rattachée à un enfant (dossier) existant.
        </p>
        {preinsc.destinataire && (
          <div className="rounded-r-md border border-border bg-surface/50 px-3 py-2 text-[11px] text-ink-subtle">
            La proposition sera adressée à : <span className="font-medium text-ink">{preinsc.destinataire}</span>
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="att-str">Structure</label>
          <select id="att-str" value={structureId} onChange={(e) => setStructureId(e.target.value)} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
            {structures.filter((s) => s.actif).map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="att-deb">Début</label>
            <input id="att-deb" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="att-h">Heures/mois</label>
            <input id="att-h" value={heures} onChange={(e) => setHeures(e.target.value)} inputMode="decimal" placeholder="140" className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="att-t">Tarif €/h</label>
            <input id="att-t" value={tarif} onChange={(e) => setTarif(e.target.value)} inputMode="decimal" placeholder="1,50" className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="att-motif">Message à la famille (optionnel)</label>
          <textarea id="att-motif" value={motif} onChange={(e) => setMotif(e.target.value)} rows={2}
            placeholder="Ex. accueil proposé sur un volume horaire réduit par rapport à votre demande…"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <p className="mt-1 text-[10px] text-ink-subtle">Ce message sera visible par la famille dans son espace, à côté de la demande acceptée.</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void submit()} disabled={saving || !dateDebut}>{saving ? 'Attribution…' : 'Attribuer & générer le contrat'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Tarifs (barème PSU / tarif de repli) ─────────────────────────────────────
// Aligné sur la place de l'onglet « Tarifs » du scolaire. La petite enfance
// facture au barème PSU (CAF) ou à un tarif horaire simple.

function TarifsPe({ setError }: { setError: (s: string | null) => void }) {
  const [params, setParams] = useState<PeParametres | null>(null);
  const [bareme, setBareme] = useState<PeBaremePsuLigne[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([
        api.petiteEnfance.facturation.parametres(),
        api.petiteEnfance.facturation.baremePsu().catch(() => ({ bareme: [] as PeBaremePsuLigne[] })),
      ]);
      setParams(p);
      setBareme(b.bareme ?? []);
    } catch { setError('Impossible de charger les tarifs.'); }
    finally { setLoading(false); }
  }, [setError]);
  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div></Card>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <LogoMark icon={Wallet} accent="petite_enfance" size="md" />
        <div>
          <SectionEyebrow as="div">Petite enfance</SectionEyebrow>
          <h2 className="font-display text-xl font-semibold text-ink">Grille tarifaire</h2>
        </div>
      </div>

      <Card variant="glass" padded>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-ink">Mode de tarification</div>
            <div className="text-[11px] text-ink-subtle">
              {params?.psuEnabled
                ? 'Barème PSU (CAF) — tarif horaire calculé selon les ressources et le taux d’effort.'
                : 'Tarif horaire simple — le tarif du contrat s’applique.'}
            </div>
          </div>
          <Badge variant={params?.psuEnabled ? 'success' : 'neutral'} size="sm">
            {params?.psuEnabled ? 'PSU activé' : 'Tarif simple'}
          </Badge>
        </div>
        {params?.tarifHoraireDefautEuros != null && (
          <div className="mt-3 border-t border-border pt-3 text-sm text-ink-muted">
            Tarif horaire de repli : <span className="font-medium text-ink">{params.tarifHoraireDefautEuros.toFixed(2)} €/h</span>
          </div>
        )}
        <p className="mt-3 text-[11px] text-ink-subtle">
          Le mode et le tarif de repli se règlent dans l’onglet <span className="font-medium">Paramètres</span>.
        </p>
      </Card>

      <Card variant="glass" padded>
        <div className="mb-2 text-sm font-medium text-ink">Barème PSU (taux d’effort par tranche)</div>
        {bareme.length > 0 ? (
          <div className="mb-3 space-y-1.5">
            {bareme.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-r-md border border-border bg-surface/40 px-3 py-2 text-sm">
                <span className="text-ink">{b.nbEnfants} enfant{b.nbEnfants > 1 ? 's' : ''} · {b.annee}</span>
                <span className="text-ink-muted">
                  {b.tauxEffortPct.toFixed(2)} %
                  {(b.plancherEuros > 0 || b.plafondEuros > 0) && ` · ${b.plancherEuros}–${b.plafondEuros} €`}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mb-3 text-[11px] text-ink-subtle">
            Aucun barème renseigné. Ajoutez les taux d’effort CAF par nombre d’enfants (le barème CNAF
            va, par ex., de 0,06 % pour 1 enfant à 0,02 % pour 4 et plus).
          </p>
        )}
        <BaremePsuEditor onSaved={load} setError={setError} />
      </Card>
    </div>
  );
}

/** Éditeur d'une ligne de barème PSU (année · nb enfants · taux d'effort · plancher/plafond). */
function BaremePsuEditor({ onSaved, setError }: { onSaved: () => Promise<void>; setError: (s: string | null) => void }) {
  const anneeCourante = new Date().getFullYear();
  const [annee, setAnnee] = useState(String(anneeCourante));
  const [nbEnfants, setNbEnfants] = useState('1');
  const [taux, setTaux] = useState('');
  const [plancher, setPlancher] = useState('');
  const [plafond, setPlafond] = useState('');
  const [saving, setSaving] = useState(false);

  const ajouter = async () => {
    const t = parseFloat(taux.replace(',', '.'));
    if (!Number.isFinite(t)) { setError('Taux d’effort invalide.'); return; }
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.facturation.setBaremePsu({
        annee: parseInt(annee, 10) || anneeCourante,
        nbEnfants: parseInt(nbEnfants, 10) || 1,
        tauxEffortPct: t,
        plancherEuros: parseFloat(plancher.replace(',', '.')) || 0,
        plafondEuros: parseFloat(plafond.replace(',', '.')) || 0,
      });
      setTaux(''); setPlancher(''); setPlafond('');
      await onSaved();
    } catch { setError('Enregistrement du barème impossible.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-r-md border border-dashed border-border p-3">
      <div className="mb-2 text-xs font-medium text-ink-muted">Ajouter / mettre à jour une tranche</div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <label className="block">
          <span className="mb-0.5 block text-[10px] text-ink-subtle">Année</span>
          <input value={annee} onChange={(e) => setAnnee(e.target.value)} inputMode="numeric"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-1.5 text-sm text-ink focus:outline-none" />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[10px] text-ink-subtle">Nb enfants</span>
          <input value={nbEnfants} onChange={(e) => setNbEnfants(e.target.value)} inputMode="numeric"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-1.5 text-sm text-ink focus:outline-none" />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[10px] text-ink-subtle">Taux %</span>
          <input value={taux} onChange={(e) => setTaux(e.target.value)} inputMode="decimal" placeholder="0,06"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-1.5 text-sm text-ink focus:outline-none" />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[10px] text-ink-subtle">Plancher €</span>
          <input value={plancher} onChange={(e) => setPlancher(e.target.value)} inputMode="decimal" placeholder="0"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-1.5 text-sm text-ink focus:outline-none" />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[10px] text-ink-subtle">Plafond €</span>
          <input value={plafond} onChange={(e) => setPlafond(e.target.value)} inputMode="decimal" placeholder="0"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-1.5 text-sm text-ink focus:outline-none" />
        </label>
      </div>
      <div className="mt-2 flex justify-end">
        <Button type="button" variant="petite_enfance" size="sm" disabled={saving || !taux.trim()} onClick={() => void ajouter()}>
          {saving ? 'Enregistrement…' : 'Enregistrer la tranche'}
        </Button>
      </div>
    </div>
  );
}

// ── Calendrier d'ouverture (réel, par structure) ─────────────────────────────

function pad2(n: number): string { return String(n).padStart(2, '0'); }
function ymd(y: number, m: number, d: number): string { return `${y}-${pad2(m + 1)}-${pad2(d)}`; }
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const JOURS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function CalendrierPe({ structures, setError }: { structures: PeStructure[]; setError: (s: string | null) => void }) {
  const today = new Date();
  const [structureId, setStructureId] = useState<string>(structures[0]?.id ?? '');
  const [annee, setAnnee] = useState(today.getFullYear());
  const [mois, setMois] = useState(today.getMonth()); // 0-11
  const [jours, setJours] = useState<PeJourOuverture[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogDate, setDialogDate] = useState<string | null>(null);
  const [motif, setMotif] = useState('');

  const load = useCallback(async () => {
    if (!structureId) return;
    setLoading(true);
    try {
      const debut = ymd(annee, mois, 1);
      const dernier = new Date(annee, mois + 1, 0).getDate();
      const fin = ymd(annee, mois, dernier);
      setJours((await api.petiteEnfance.calendrier.list(structureId, debut, fin)).jours);
    } catch { setError('Impossible de charger le calendrier.'); }
    finally { setLoading(false); }
  }, [structureId, annee, mois, setError]);
  useEffect(() => { void load(); }, [load]);

  const moisPrec = () => { if (mois === 0) { setMois(11); setAnnee((a) => a - 1); } else setMois((m) => m - 1); };
  const moisSuiv = () => { if (mois === 11) { setMois(0); setAnnee((a) => a + 1); } else setMois((m) => m + 1); };

  const jourMap = new Map(jours.map((j) => [j.date, j]));
  const premierJourSemaine = (new Date(annee, mois, 1).getDay() + 6) % 7; // lundi=0
  const nbJours = new Date(annee, mois + 1, 0).getDate();
  const cases: (number | null)[] = [
    ...Array.from({ length: premierJourSemaine }, () => null),
    ...Array.from({ length: nbJours }, (_, i) => i + 1),
  ];

  const ouvrirJour = (jour: number) => {
    const date = ymd(annee, mois, jour);
    const existant = jourMap.get(date);
    setMotif(existant?.motif ?? '');
    setDialogDate(date);
  };

  const marquer = async (ouvert: boolean) => {
    if (!dialogDate) return;
    setError(null);
    try {
      await api.petiteEnfance.calendrier.set({ structureId, date: dialogDate, ouvert, motif: motif.trim() || null });
      setDialogDate(null);
      await load();
    } catch { setError('Enregistrement impossible.'); }
  };

  const reinitialiser = async () => {
    if (!dialogDate) return;
    const existant = jourMap.get(dialogDate);
    if (!existant) { setDialogDate(null); return; }
    setError(null);
    try {
      await api.petiteEnfance.calendrier.remove(existant.id);
      setDialogDate(null);
      await load();
    } catch { setError('Suppression impossible.'); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-3">
        <LogoMark icon={CalendarClock} accent="petite_enfance" size="md" />
        <div>
          <SectionEyebrow as="div">Petite enfance</SectionEyebrow>
          <h2 className="font-display text-xl font-semibold text-ink">Calendrier d'ouverture</h2>
          <p className="text-[11px] text-ink-subtle">Cliquez un jour pour le fermer (vacances, pont) ou le rouvrir.</p>
        </div>
      </div>

      {structures.length === 0 ? (
        <Card variant="glass" padded><p className="py-6 text-center text-sm text-ink-subtle">Créez d'abord une structure (onglet Structures).</p></Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <select value={structureId} onChange={(e) => setStructureId(e.target.value)}
              className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              {structures.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
            <div className="ml-auto flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={moisPrec} aria-label="Mois précédent"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="min-w-[140px] text-center text-sm font-medium capitalize text-ink">{MOIS_FR[mois]} {annee}</span>
              <Button type="button" variant="ghost" size="sm" onClick={moisSuiv} aria-label="Mois suivant"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>

          <Card variant="glass" padded>
            <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-ink-subtle">
              {JOURS_FR.map((j) => <div key={j}>{j}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cases.map((jour, i) => {
                if (jour === null) return <div key={`v-${i}`} />;
                const date = ymd(annee, mois, jour);
                const marque = jourMap.get(date);
                const isWeekend = [5, 6].includes((new Date(annee, mois, jour).getDay() + 6) % 7);
                let cls = 'border-border bg-surface/40 text-ink hover:bg-surface-hi';
                if (marque?.ouvert === false) cls = 'border-urgent/40 bg-urgent/10 text-urgent';
                else if (marque?.ouvert === true) cls = 'border-success/40 bg-success/10 text-success';
                else if (isWeekend) cls = 'border-border bg-surface/20 text-ink-subtle';
                return (
                  <button key={date} type="button" onClick={() => ouvrirJour(jour)}
                    className={`flex aspect-square flex-col items-center justify-center rounded-r-md border text-sm transition-colors ${cls}`}
                    title={marque?.motif ?? ''}>
                    <span>{jour}</span>
                    {marque?.ouvert === false && <X className="h-3 w-3" aria-hidden="true" />}
                    {marque?.ouvert === true && <Check className="h-3 w-3" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
            {loading && <p className="mt-2 text-center text-[11px] text-ink-subtle">Chargement…</p>}
            <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-ink-subtle">
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm border border-urgent/40 bg-urgent/10" /> Fermé</span>
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm border border-success/40 bg-success/10" /> Ouvert (forcé)</span>
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm border border-border bg-surface/40" /> Par défaut</span>
            </div>
          </Card>
        </>
      )}

      <Dialog open={!!dialogDate} onClose={() => setDialogDate(null)} title={dialogDate ? `Jour du ${new Date(dialogDate + 'T00:00:00').toLocaleDateString('fr-FR')}` : ''} size="sm"
        footer={<>
          <Button variant="ghost" size="sm" onClick={() => setDialogDate(null)}>Annuler</Button>
          {dialogDate && jourMap.has(dialogDate) && <Button variant="secondary" size="sm" onClick={() => void reinitialiser()}>Réinitialiser</Button>}
          <Button variant="danger" size="sm" onClick={() => void marquer(false)}>Fermer ce jour</Button>
          <Button variant="success" size="sm" onClick={() => void marquer(true)}>Ouvrir ce jour</Button>
        </>}>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="cal-motif">Motif (optionnel)</label>
          <input id="cal-motif" value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. Fermeture annuelle, pont…"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
        </div>
      </Dialog>
    </div>
  );
}

// ── Paramètres (regroupés, jumeaux du scolaire) ──────────────────────────────
// Tous les réglages du module au même endroit. Réservé admin (maire/adjoints) —
// garde-fou de route dans le composant page + requireRole côté backend.

function ParametresPe({ setError }: { setError: (s: string | null) => void }) {
  const [params, setParams] = useState<PeParametres | null>(null);
  const [loading, setLoading] = useState(true);
  const [psu, setPsu] = useState(false);
  const [tarifRepli, setTarifRepli] = useState('');
  const [uniteMin, setUniteMin] = useState<15 | 30 | 60>(30);
  const [arrondi, setArrondi] = useState<'sup' | 'proche'>('sup');
  const [ampDebut, setAmpDebut] = useState('');
  const [ampFin, setAmpFin] = useState('');
  // Règles d'absence
  const [absPrev, setAbsPrev] = useState('2');
  const [absTraitement, setAbsTraitement] = useState<'facture' | 'non_facture' | 'non_facture_si_justificatif'>('facture');
  const [absJustif, setAbsJustif] = useState('5');
  // Automatisation facturation
  const [autoActive, setAutoActive] = useState(false);
  const [autoJour, setAutoJour] = useState('5');
  const [autoHeure, setAutoHeure] = useState('6');
  const [autoPeriode, setAutoPeriode] = useState<'mois_ecoule' | 'mois_en_cours'>('mois_ecoule');
  const [autoEmail, setAutoEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await api.petiteEnfance.facturation.parametres();
      setParams(p);
      setPsu(p.psuEnabled ?? false);
      setTarifRepli(p.tarifHoraireDefautEuros ? String(p.tarifHoraireDefautEuros) : '');
      setUniteMin((p.facturationUniteMinutes as 15 | 30 | 60) ?? 30);
      setArrondi(p.facturationArrondi ?? 'sup');
      setAmpDebut(p.amplitudeDebut ?? '');
      setAmpFin(p.amplitudeFin ?? '');
      setAbsPrev(String(p.absenceDelaiPrevenanceJours ?? 2));
      setAbsTraitement(p.absenceTraitement ?? 'facture');
      setAbsJustif(String(p.absenceDelaiJustificatifJours ?? 5));
      setAutoActive(p.autoFacturationActive ?? false);
      setAutoJour(String(p.autoFacturationJour ?? 5));
      setAutoHeure(String(p.autoFacturationHeure ?? 6));
      setAutoPeriode(p.autoFacturationPeriode ?? 'mois_ecoule');
      setAutoEmail(p.autoFacturationEnvoiEmail ?? false);
    } catch { setError('Impossible de charger les paramètres.'); }
    finally { setLoading(false); }
  }, [setError]);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true); setError(null); setOkMsg(null);
    try {
      await api.petiteEnfance.facturation.setParametres({
        psuEnabled: psu,
        tarifHoraireDefautEuros: parseFloat(tarifRepli.replace(',', '.')) || 0,
      });
      setOkMsg('Paramètres enregistrés.');
      await load();
    } catch { setError('Enregistrement impossible.'); }
    finally { setSaving(false); }
  };

  const saveAmplitudes = async () => {
    setSaving(true); setError(null); setOkMsg(null);
    try {
      await api.petiteEnfance.facturation.setParametres({
        facturationUniteMinutes: uniteMin,
        facturationArrondi: arrondi,
        amplitudeDebut: ampDebut || null,
        amplitudeFin: ampFin || null,
      });
      setOkMsg('Amplitudes enregistrées.');
      await load();
    } catch { setError('Enregistrement impossible.'); }
    finally { setSaving(false); }
  };

  const saveAbsences = async () => {
    setSaving(true); setError(null); setOkMsg(null);
    try {
      await api.petiteEnfance.facturation.setParametres({
        absenceDelaiPrevenanceJours: parseInt(absPrev, 10) || 0,
        absenceTraitement: absTraitement,
        absenceDelaiJustificatifJours: parseInt(absJustif, 10) || 0,
      });
      setOkMsg('Règles d’absence enregistrées.');
      await load();
    } catch { setError('Enregistrement impossible.'); }
    finally { setSaving(false); }
  };

  const saveAuto = async () => {
    setSaving(true); setError(null); setOkMsg(null);
    try {
      await api.petiteEnfance.facturation.setParametres({
        autoFacturationActive: autoActive,
        autoFacturationJour: parseInt(autoJour, 10) || 5,
        autoFacturationHeure: parseInt(autoHeure, 10) || 6,
        autoFacturationPeriode: autoPeriode,
        autoFacturationEnvoiEmail: autoEmail,
      });
      setOkMsg('Automatisation enregistrée.');
      await load();
    } catch { setError('Enregistrement impossible.'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div></Card>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <LogoMark icon={Settings} accent="petite_enfance" size="md" />
        <div>
          <SectionEyebrow as="div">Petite enfance</SectionEyebrow>
          <h2 className="font-display text-xl font-semibold text-ink">Paramètres</h2>
          <p className="text-[11px] text-ink-subtle">Réservé au maire et aux adjoints.</p>
        </div>
      </div>

      {okMsg && <Alert variant="success" icon={AlertCircle}>{okMsg}</Alert>}

      <Card variant="glass" padded>
        <div className="mb-1 text-sm font-medium text-ink">Tarification (barème PSU)</div>
        <label className="mt-2 flex items-center gap-2.5">
          <input type="checkbox" checked={psu} onChange={(e) => setPsu(e.target.checked)} className="h-4 w-4" />
          <span className="text-sm text-ink">Utiliser le barème PSU (CAF)</span>
        </label>
        <p className="mt-1 text-[11px] text-ink-subtle">
          Si activé, le tarif horaire est calculé selon les ressources de la famille et le taux d'effort.
          Sinon, le tarif horaire du contrat s'applique.
        </p>
        <div className="mt-3 max-w-xs">
          <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="pp-repli">Tarif horaire de repli (€)</label>
          <input id="pp-repli" value={tarifRepli} onChange={(e) => setTarifRepli(e.target.value)} inputMode="decimal" placeholder="1,50"
            className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <p className="mt-1 text-[11px] text-ink-subtle">Utilisé si un contrat n'a pas de tarif renseigné.</p>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void save()} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </Card>

      <Card variant="glass" padded>
        <div className="mb-1 text-sm font-medium text-ink">Amplitudes d’accueil & facturation au temps</div>
        <p className="text-[11px] text-ink-subtle mb-3">
          Définit l’unité de temps facturée et la règle d’arrondi pour la facturation au temps de présence,
          ainsi que l’amplitude d’ouverture par défaut.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="pp-unite">Unité de temps facturée</label>
            <select id="pp-unite" value={uniteMin} onChange={(e) => setUniteMin(Number(e.target.value) as 15 | 30 | 60)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              <option value={15}>Au quart d’heure (15 min)</option>
              <option value={30}>À la demi-heure (30 min)</option>
              <option value={60}>À l’heure (60 min)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="pp-arrondi">Arrondi</label>
            <select id="pp-arrondi" value={arrondi} onChange={(e) => setArrondi(e.target.value as 'sup' | 'proche')}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              <option value="sup">Unité commencée due (arrondi supérieur)</option>
              <option value="proche">Au plus proche</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="pp-amp-debut">Ouverture (HH:MM)</label>
            <input id="pp-amp-debut" type="time" value={ampDebut} onChange={(e) => setAmpDebut(e.target.value)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="pp-amp-fin">Fermeture (HH:MM)</label>
            <input id="pp-amp-fin" type="time" value={ampFin} onChange={(e) => setAmpFin(e.target.value)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void saveAmplitudes()} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer les amplitudes'}
          </Button>
        </div>
      </Card>

      <Card variant="glass" padded>
        <div className="mb-1 text-sm font-medium text-ink">Pièces à fournir</div>
        <p className="text-[11px] text-ink-subtle">
          Les natures de pièces réclamées aux familles (et le choix de celles réellement demandées)
          se pilotent désormais dans <span className="text-ink">GRC → Dossiers → Dossiers petite enfance → Pièces demandées</span>.
          Une pièce déposée une fois sert tous les dossiers ; on ne redemande que ce qui manque.
        </p>
      </Card>

      <Card variant="glass" padded>
        <div className="mb-1 text-sm font-medium text-ink">Règles d’absence</div>
        <p className="text-[11px] text-ink-subtle mb-3">
          Comment traiter les absences au regard de la facturation.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="abs-prev">Délai de prévenance (jours)</label>
            <input id="abs-prev" value={absPrev} onChange={(e) => setAbsPrev(e.target.value)} inputMode="numeric"
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="abs-trait">Absence non prévenue</label>
            <select id="abs-trait" value={absTraitement} onChange={(e) => setAbsTraitement(e.target.value as typeof absTraitement)}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              <option value="facture">Facturée (pas de déduction)</option>
              <option value="non_facture">Non facturée (toujours déduite)</option>
              <option value="non_facture_si_justificatif">Déduite seulement sur justificatif</option>
            </select>
          </div>
          {absTraitement === 'non_facture_si_justificatif' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="abs-justif">Délai justificatif (jours)</label>
              <input id="abs-justif" value={absJustif} onChange={(e) => setAbsJustif(e.target.value)} inputMode="numeric"
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void saveAbsences()} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer les règles'}
          </Button>
        </div>
      </Card>

      <Card variant="glass" padded>
        <div className="mb-1 text-sm font-medium text-ink">Automatisation de la facturation</div>
        <p className="text-[11px] text-ink-subtle mb-3">
          Émission mensuelle automatique des factures petite enfance. La planification effective dépend du
          Cron VPC (côté serveur) ; cette config définit le déclenchement souhaité.
        </p>
        <label className="mb-3 flex items-center gap-2.5">
          <input type="checkbox" checked={autoActive} onChange={(e) => setAutoActive(e.target.checked)} className="h-4 w-4" />
          <span className="text-sm text-ink">Activer l’émission automatique</span>
        </label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="auto-jour">Jour du mois</label>
            <input id="auto-jour" value={autoJour} onChange={(e) => setAutoJour(e.target.value)} inputMode="numeric" disabled={!autoActive}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none disabled:opacity-50" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="auto-heure">Heure</label>
            <input id="auto-heure" value={autoHeure} onChange={(e) => setAutoHeure(e.target.value)} inputMode="numeric" disabled={!autoActive}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none disabled:opacity-50" />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="auto-periode">Période facturée</label>
            <select id="auto-periode" value={autoPeriode} onChange={(e) => setAutoPeriode(e.target.value as typeof autoPeriode)} disabled={!autoActive}
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none disabled:opacity-50">
              <option value="mois_ecoule">Mois écoulé</option>
              <option value="mois_en_cours">Mois en cours</option>
            </select>
          </div>
        </div>
        <label className="mt-3 flex items-center gap-2.5">
          <input type="checkbox" checked={autoEmail} onChange={(e) => setAutoEmail(e.target.checked)} className="h-4 w-4" disabled={!autoActive} />
          <span className="text-sm text-ink">Envoyer la facture par e-mail aux familles</span>
        </label>
        {params?.autoFacturationLastRun && (
          <p className="mt-2 text-[11px] text-ink-subtle">
            Dernière exécution : {new Date(params.autoFacturationLastRun).toLocaleString('fr-FR')}
          </p>
        )}
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="petite_enfance" size="sm" onClick={() => void saveAuto()} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer l’automatisation'}
          </Button>
        </div>
      </Card>

      <Card variant="glass" padded>
        <div className="mb-1 text-sm font-medium text-ink">Facture & en-tête</div>
        <p className="text-[11px] text-ink-subtle">
          L’en-tête de facture, la mention de pied, l’IBAN de la régie et le délai de paiement sont
          <span className="font-medium text-ink"> communs à toute la collectivité</span> (mairie). Ils se
          configurent une seule fois dans le module Scolaire / Facturation et s’appliquent automatiquement
          aux factures petite enfance.
        </p>
      </Card>
    </div>
  );
}
